"use strict";
/*
 * Web-based implementation of agnostic Firebase wrapper.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.FirebaseWebFunc = exports.FirebaseWebUser = exports.FirestoreWebQuerySnapshot = exports.FirestoreWebDocSnapshot = exports.FirestoreWebDocRef = exports.FirestoreWebCollectionRef = exports.FirestoreWebBatch = exports.FirestoreWebTX = exports.FirestoreWeb = exports.FirebaseWebAuth = void 0;
exports.firebaseConnectWebFirestore = firebaseConnectWebFirestore;
const app_1 = require("firebase/app");
const auth_1 = require("firebase/auth");
const functions_1 = require("firebase/functions");
const firestore_1 = require("firebase/firestore");
const FirebaseV9_CommonImpl_1 = require("./FirebaseV9-CommonImpl");
const Cleanups_1 = require("./Cleanups");
const anyerror_1 = require("./anyerror");
/**
 * Convert a partial persistent document into the "patch" format (an object of
 * dotted-path properties).  Convert any embedded FieldPlaceholder entries into
 * FieldValue entries.
 */
function partialToUpdateDoc(ctx, data) {
    const patch = (0, FirebaseV9_CommonImpl_1.partialToPatch)(ctx, data, (fp) => {
        ctx.assertTrue(fp.isIncrement());
        return (0, firestore_1.increment)(fp.getParam());
    });
    /* The result is an UpdateData doc. */
    return patch;
}
function firebaseConnectWebFirestore(ctx, firebaseConfig, emulator) {
    const useEmu = emulator.useEmulator;
    const cleanups = new Cleanups_1.Cleanups();
    const app = (0, app_1.initializeApp)(firebaseConfig);
    cleanups.push(() => (0, app_1.deleteApp)(app));
    ctx.log('Inititalized Web Firebase SDK', useEmu ? '(prod)' : '(EMULATOR)');
    const db = (0, firestore_1.getFirestore)(app);
    cleanups.push(() => (0, firestore_1.terminate)(db));
    if (useEmu) {
        (0, firestore_1.connectFirestoreEmulator)(db, emulator.storeEmulator.host, emulator.storeEmulator.port);
    }
    const auth = (0, auth_1.getAuth)(app);
    if (useEmu) {
        const authEmCfg = emulator.authEmulator;
        (0, auth_1.connectAuthEmulator)(auth, `http://${authEmCfg.host}:${authEmCfg.port}`);
    }
    const funcClient = (0, functions_1.getFunctions)(app);
    if (useEmu) {
        const emCfg = emulator.funcEmulator;
        (0, functions_1.connectFunctionsEmulator)(funcClient, emCfg.host, emCfg.port);
    }
    return {
        firestore: new FirestoreWeb(db),
        auth: new FirebaseWebAuth(auth, useEmu),
        funcClient: new FirebaseWebFunc(funcClient),
        cleanups,
    };
}
class FirebaseWebAuth {
    constructor(auth, usingEmulator) {
        this.auth = auth;
        this.emu = usingEmulator;
    }
    usingEmulator() {
        return this.emu;
    }
    currentUser() {
        const cu = this.auth.currentUser;
        if (cu === null) {
            return null;
        }
        return new FirebaseWebUser(cu);
    }
    userFromCreds(ctx, name, userCreds) {
        const user = userCreds.user;
        if (!user) {
            throw ctx.newError(`No user.  Creating "${name}", got:`, user);
        }
        return new FirebaseWebUser(user);
    }
    /**
     * Web clients aren't allowed to inspect users, so return null here, unless they're
     * asking about themselves.
     */
    fetchUser(id) {
        const curr = this.auth.currentUser;
        if (id === (curr === null || curr === void 0 ? void 0 : curr.uid)) {
            return Promise.resolve(new FirebaseWebUser(curr));
        }
        return Promise.reject(new Error(`Cannot 'fetch', current user is not "${id}"`));
    }
    /**
     * Web clients aren't allowed to inspect users, so return null here, unless they're
     * asking about themselves.
     */
    fetchUserByEmail(_email) {
        // Not available in Web
        return Promise.reject(new Error('getUserByEmail not available in Web API'));
    }
    /**
     * Create given user, if necessary.
     *
     * @return <user>|'exists', depending.
     */
    createUser(ctx, username, password) {
        ctx.log('Creating account', username, 'and logging in as it.');
        return (0, auth_1.createUserWithEmailAndPassword)(this.auth, username, password)
            .then((userCreds) => {
            return this.userFromCreds(ctx, username, userCreds);
        }) //
            .catch((err) => {
            const errCode = (0, anyerror_1.extractErrorProperty)(err, 'code');
            if (errCode === 'auth/email-already-exists' || errCode === 'auth/email-already-in-use') {
                return 'exists';
            }
            throw err;
        });
    }
    signInWithGoog(ctx) {
        const googProvider = new auth_1.GoogleAuthProvider();
        googProvider.addScope('email');
        return (0, auth_1.signInWithPopup)(this.auth, googProvider)
            .then((result) => {
            // The signed-in user info.
            const user = result.user;
            return new FirebaseWebUser(user);
        }).catch((err) => {
            // The email of the user's account used.
            // const email = err.customData.email;
            // The AuthCredential type that was used.
            // const credential = GoogleAuthProvider.credentialFromError(err);
            throw ctx.chainError(err, 'Google sign-in');
        });
    }
    signInUserPassword(ctx, username, password) {
        return (0, auth_1.signInWithEmailAndPassword)(this.auth, username, password)
            .then(userCreds => {
            return this.userFromCreds(ctx, username, userCreds);
        })
            .catch((err) => {
            throw ctx.chainError(err, `Signing in "${username}"`);
        });
    }
    authUserFromRequest(ctx, _uid) {
        return Promise.reject(ctx.newError('auth user login unsupported on Web'));
    }
    signOutCurrent(_ctx) {
        return (0, auth_1.signOut)(this.auth);
    }
    onAuthStateChanged(ctx, cb) {
        this.auth.onAuthStateChanged(user => {
            if (user === null) {
                cb(null);
            }
            else {
                const wrappedUser = new FirebaseWebUser(user);
                cb(wrappedUser);
            }
        });
    }
    verifyIdToken(ctx, _s) {
        return Promise.reject(ctx.newError('no id verify on Web client'));
    }
    sendPasswordResetEmail(ctx, email) {
        ctx.log('Sending password reset email ...');
        return (0, auth_1.sendPasswordResetEmail)(this.auth, email)
            .then(() => {
            ctx.log('Password reset email sent (AFAICT)');
        })
            .catch((err) => {
            throw ctx.chainError(err, 'Sending password reset email');
        });
    }
}
exports.FirebaseWebAuth = FirebaseWebAuth;
class FirestoreWeb {
    constructor(db) {
        this.db = db;
    }
    makeCollectionRef(ctx, name) {
        const c = (0, firestore_1.collection)(this.db, name);
        return new FirestoreWebCollectionRef(c);
    }
    waitForPendingWrites(_ctx) {
        return (0, firestore_1.waitForPendingWrites)(this.db);
    }
    /**
     * Run given callback in a transaction (it may be executed more than once).
     *
     * Do not invoke directly, invoke via {@link firestoreTransaction}.
     *
     * @param ctx
     * @param callback
     * @returns
     */
    _runInTransaction(ctx, callback) {
        const db = this.db;
        // XXX RETRY?
        return (0, firestore_1.runTransaction)(db, (webTx) => {
            const txWrapper = new FirestoreWebTX(webTx);
            return callback(txWrapper);
        });
    }
    // Invoke via {@link firestoreWriteBatch}
    _startWriteBatch(_ctx) {
        const b = (0, firestore_1.writeBatch)(this.db);
        return new FirestoreWebBatch(b);
    }
}
exports.FirestoreWeb = FirestoreWeb;
class FirestoreWebTX {
    constructor(webTx) {
        this.webTx = webTx;
    }
    getDoc(ctx, docRef) {
        return this.webTx.get(docRef.docRef).then(qsn => {
            return new FirestoreWebDocSnapshot(qsn);
        });
    }
    setDoc(_ctx, docRef, data) {
        this.webTx.set(docRef.docRef, data);
    }
    patchIn(ctx, docRef, data) {
        this.webTx.update(docRef.docRef, partialToUpdateDoc(ctx, data));
    }
}
exports.FirestoreWebTX = FirestoreWebTX;
class FirestoreWebBatch {
    constructor(b) {
        this.batch = b;
    }
    setDoc(ctx, docRef, data) {
        this.batch.set(docRef.docRef, data);
    }
    patchIn(ctx, docRef, data) {
        this.batch.update(docRef.docRef, partialToUpdateDoc(ctx, data));
    }
    commit(_ctx) {
        // XXX invoke RETRY on error?
        return this.batch.commit();
    }
}
exports.FirestoreWebBatch = FirestoreWebBatch;
class FirestoreWebCollectionRef {
    constructor(collection) {
        this.collection = collection;
    }
    debugString() {
        return this.collection.path;
    }
    makeDocRef(name) {
        const d = (0, firestore_1.doc)(this.collection, name);
        return new FirestoreWebDocRef(d);
    }
    newDocRef() {
        const d = (0, firestore_1.doc)(this.collection);
        return new FirestoreWebDocRef(d);
    }
    getDocs() {
        // XXX RETRY?
        return (0, firestore_1.getDocs)(this.collection).then(snapshot => {
            return new FirestoreWebQuerySnapshot(snapshot);
        });
    }
    getDocsWhere(path, op, value) {
        const q = (0, firestore_1.query)(this.collection, (0, firestore_1.where)(path, op, value));
        // XXX RETRY?
        return (0, firestore_1.getDocs)(q).then(snapshot => {
            return new FirestoreWebQuerySnapshot(snapshot);
        });
    }
    getCount() {
        return (0, firestore_1.getCountFromServer)(this.collection).then(snapshot => {
            return snapshot.data().count;
        });
    }
    // Listen for updates on this collection
    onSnapshot(cb, errCb) {
        return (0, firestore_1.onSnapshot)(this.collection, snap => cb(new FirestoreWebQuerySnapshot(snap)), errCb);
    }
    onSnapshotWhere(path, op, value, cb, errCb) {
        const q = (0, firestore_1.query)(this.collection, (0, firestore_1.where)(path, op, value));
        return (0, firestore_1.onSnapshot)(q, snap => cb(new FirestoreWebQuerySnapshot(snap)), errCb);
    }
}
exports.FirestoreWebCollectionRef = FirestoreWebCollectionRef;
class FirestoreWebDocRef {
    constructor(docRef) {
        this.docRef = docRef;
    }
    id() {
        return this.docRef.id;
    }
    debugString() {
        return this.docRef.path;
    }
    getFirestore() {
        return new FirestoreWeb(this.docRef.firestore);
    }
    makeCollectionRef(_ctx, name) {
        // XXX: Why the odd casting of return value here?
        const c = (0, firestore_1.collection)(this.docRef, name);
        return new FirestoreWebCollectionRef(c);
    }
    get() {
        // XXX RETRY?
        return (0, firestore_1.getDoc)(this.docRef).then(fbRef => new FirestoreWebDocSnapshot(fbRef));
    }
    set(ctx, data) {
        // XXX RETRY?
        return (0, firestore_1.setDoc)(this.docRef, data);
    }
    patchIn(ctx, data) {
        // XXX RETRY?
        return (0, firestore_1.updateDoc)(this.docRef, partialToUpdateDoc(ctx, data))
            .catch((err) => {
            throw ctx.chainError(err, `patching ${this.docRef.path}`);
        });
    }
    // Listen for updates on this document
    onSnapshot(cb, errCb) {
        return (0, firestore_1.onSnapshot)(this.docRef, snap => cb(new FirestoreWebDocSnapshot(snap)), errCb);
    }
}
exports.FirestoreWebDocRef = FirestoreWebDocRef;
class FirestoreWebDocSnapshot {
    constructor(snap) {
        this.snap = snap;
    }
    id() {
        return this.snap.id;
    }
    ref() {
        return new FirestoreWebDocRef(this.snap.ref);
    }
    docExists() {
        return this.snap.exists();
    }
    hasPendingContent() {
        return this.snap.metadata.hasPendingWrites;
    }
    data() {
        return this.snap.data();
    }
}
exports.FirestoreWebDocSnapshot = FirestoreWebDocSnapshot;
class FirestoreWebQuerySnapshot {
    constructor(snap) {
        this.snap = snap;
    }
    size() {
        return this.snap.size;
    }
    forEach(cb) {
        this.snap.forEach(webDoc => {
            cb(new FirestoreWebDocSnapshot(webDoc));
        });
    }
}
exports.FirestoreWebQuerySnapshot = FirestoreWebQuerySnapshot;
class FirebaseWebUser {
    constructor(user) {
        this.user = user;
    }
    uid() {
        return this.user.uid;
    }
    email() {
        var _a;
        return (_a = this.user.email) !== null && _a !== void 0 ? _a : 'no-email-address';
    }
    displayName() {
        var _a;
        return (_a = this.user.displayName) !== null && _a !== void 0 ? _a : this.email();
    }
    providerId() {
        return this.user.providerId;
    }
    getIdToken() {
        return this.user.getIdToken();
    }
    debugString() {
        return `${JSON.stringify(this.user)}`;
    }
}
exports.FirebaseWebUser = FirebaseWebUser;
class FirebaseWebFunc {
    constructor(funcs) {
        this.funcs = funcs;
    }
    findCallable(name) {
        const callable = (0, functions_1.httpsCallable)(this.funcs, name);
        return (args) => {
            return callable(args).then(result => {
                return result.data;
            });
        };
    }
}
exports.FirebaseWebFunc = FirebaseWebFunc;
//# sourceMappingURL=FirebaseV9-WebImpl.js.map